import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCondos } from "context/CondosContext";
import { useAuth } from "context/AuthContext";
import { MultiSelect } from "components/ui/MultiSelect";
import { Select } from "components/ui/Select";
import Spinner from "components/ui/Spinner";
import fetchWithCredentials, {
  isPresignedUrl,
  regenerateFileUrl,
} from "apiClient";
import {
  CondoDocumentGroup,
  CondoDocumentsResponse,
} from "types/CondoDocument";
import { ExternalLink, Trash2 } from "lucide-react";
import { cn } from "lib/utils";
import { formatTimestamp } from "utils/dateFormat";
import { MOBILE_PAGE_TITLE_MENU_INSET_COMFORT } from "constants/pageLayout";

type Locale = "es" | "en";

function typeSelectValueToParams(value: string): {
  type?: string;
  otherType?: string;
} {
  if (!value) return {};
  if (value === "spreadsheet") return { type: "spreadsheet" };
  if (value === "other") return { type: "other" };
  if (value.startsWith("other:")) {
    const rest = value.slice("other:".length);
    return rest ? { type: "other", otherType: rest } : { type: "other" };
  }
  return { type: value };
}

function groupKey(g: CondoDocumentGroup): string {
  return [
    g.condo.id,
    g.type,
    g.otherType ?? "",
    g.unit?.id ?? "",
    g.provider?.id ?? "",
    g.spreadsheetId ?? "",
  ].join(":");
}

export default function Documents() {
  const { condos } = useCondos();
  const { lang } = useAuth();
  const locale: Locale = lang === "es" ? "es" : "en";

  const [selectedCondoIds, setSelectedCondoIds] = useState<number[]>([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");

  const [groups, setGroups] = useState<CondoDocumentGroup[]>([]);
  const [filterOptions, setFilterOptions] =
    useState<CondoDocumentsResponse["filterOptions"]>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const buildingOptions = useMemo(() => {
    return [
      {
        value: "all",
        label: locale === "es" ? "Todos los edificios" : "All buildings",
      },
      ...condos.map((condo) => ({
        value: String(condo.id),
        label: condo.name,
      })),
    ];
  }, [condos, locale]);

  const selectedBuildingValues = selectedCondoIds.map((id) => String(id));

  const handleBuildingChange = (values: string[]) => {
    setSelectedCondoIds(
      values.map((v) => parseInt(v, 10)).filter((n) => !Number.isNaN(n)),
    );
  };

  const typeOptions = useMemo(() => {
    const allLabel = locale === "es" ? "Todos los tipos" : "All types";
    const otherAny = locale === "es" ? "Otro (cualquiera)" : "Other (any)";

    const base: { value: string; label: string }[] = [
      { value: "", label: allLabel },
      {
        value: "common_expenses",
        label: locale === "es" ? "Gastos comunes" : "Common expenses",
      },
      {
        value: "meeting_minutes",
        label: locale === "es" ? "Acta de reunión" : "Meeting minutes",
      },
      {
        value: "bylaws",
        label: locale === "es" ? "Reglamento" : "Bylaws",
      },
      {
        value: "lease_agreement",
        label: locale === "es" ? "Contrato de alquiler" : "Lease agreement",
      },
      {
        value: "provider_invoice",
        label: locale === "es" ? "Factura de proveedor" : "Provider invoice",
      },
      {
        value: "spreadsheet",
        label: locale === "es" ? "Hoja de cálculo" : "Spreadsheet",
      },
      { value: "other", label: otherAny },
    ];

    if (filterOptions?.typeOptions && selectedCondoIds.length === 1) {
      const seen = new Set(base.map((o) => o.value));
      for (const opt of filterOptions.typeOptions) {
        if (
          opt.type !== "other" ||
          opt.otherType == null ||
          opt.otherType === ""
        )
          continue;
        const v = `other:${opt.otherType}`;
        if (!seen.has(v)) {
          seen.add(v);
          base.push({
            value: v,
            label:
              locale === "es"
                ? `Otro: ${opt.otherType}`
                : `Other: ${opt.otherType}`,
          });
        }
      }
    }

    return base;
  }, [filterOptions, locale, selectedCondoIds.length]);

  const unitOptions = useMemo(() => {
    const all = locale === "es" ? "Todas las unidades" : "All units";
    const opts = [{ value: "", label: all }];
    if (filterOptions?.units) {
      for (const u of filterOptions.units) {
        opts.push({ value: String(u.id), label: u.name });
      }
    }
    return opts;
  }, [filterOptions, locale]);

  const providerOptions = useMemo(() => {
    const all = locale === "es" ? "Todos los proveedores" : "All providers";
    const opts = [{ value: "", label: all }];
    if (filterOptions?.providers) {
      for (const p of filterOptions.providers) {
        opts.push({ value: String(p.id), label: p.name });
      }
    }
    return opts;
  }, [filterOptions, locale]);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      for (const id of selectedCondoIds) {
        params.append("condo", String(id));
      }
      const tf = typeSelectValueToParams(typeFilter);
      if (tf.type) params.set("type", tf.type);
      if (tf.otherType) params.set("other_type", tf.otherType);
      if (unitFilter) params.set("unit", unitFilter);
      if (providerFilter) params.set("provider", providerFilter);
      params.set("include_filter_options", "1");

      const url = `/manager-api/condo-documents/?${params.toString()}`;
      const data: CondoDocumentsResponse = await fetchWithCredentials(url);
      setGroups(data.groups ?? []);
      setFilterOptions(data.filterOptions ?? null);
    } catch (err: unknown) {
      console.error(err);
      setError(
        err instanceof Error
          ? err.message
          : locale === "es"
            ? "Error al cargar documentos"
            : "Failed to load documents",
      );
    } finally {
      setLoading(false);
    }
  }, [selectedCondoIds, typeFilter, unitFilter, providerFilter, locale]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  /** Unit filter needs exactly one building; auto-select when there is only one. */
  useEffect(() => {
    if (condos.length === 1 && selectedCondoIds.length === 0) {
      setSelectedCondoIds([condos[0].id]);
    }
  }, [condos, selectedCondoIds.length]);

  useEffect(() => {
    if (selectedCondoIds.length !== 1) {
      setUnitFilter("");
    }
  }, [selectedCondoIds]);

  const handleDeleteVersion = async (condoDocumentId: number) => {
    const msg =
      locale === "es"
        ? "¿Eliminar esta versión del documento? Esta acción no se puede deshacer."
        : "Delete this document version? This cannot be undone.";
    if (!window.confirm(msg)) {
      return;
    }
    setDeletingId(condoDocumentId);
    try {
      await fetchWithCredentials(
        `/manager-api/condo-documents/${condoDocumentId}/`,
        { method: "DELETE" },
      );
      await fetchDocuments();
    } catch (err: unknown) {
      console.error(err);
      window.alert(
        locale === "es"
          ? "No se pudo eliminar el documento."
          : "Could not delete the document.",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteSpreadsheet = async (spreadsheetRowId: number) => {
    const msg =
      locale === "es"
        ? "¿Quitar esta hoja de cálculo de la lista? El archivo en Google no se elimina."
        : "Remove this spreadsheet from the list? The file in Google will not be deleted.";
    if (!window.confirm(msg)) {
      return;
    }
    setDeletingId(spreadsheetRowId);
    try {
      await fetchWithCredentials(
        `/manager-api/condo-spreadsheets/${spreadsheetRowId}/`,
        { method: "DELETE" },
      );
      await fetchDocuments();
    } catch (err: unknown) {
      console.error(err);
      window.alert(
        locale === "es"
          ? "No se pudo eliminar el registro."
          : "Could not remove the spreadsheet.",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const title = locale === "es" ? "Documentos" : "Documents";

  return (
    <div className="w-full h-full max-w-7xl overflow-y-auto bg-white py-4 max-lg:pl-0 max-lg:pr-4 lg:px-6">
      <h1
        className={`mb-6 text-2xl font-bold text-gray-900 ${MOBILE_PAGE_TITLE_MENU_INSET_COMFORT}`}
      >
        {title}
      </h1>

      <div className="max-lg:pl-4 lg:pl-0 space-y-6">
        <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {locale === "es" ? "Edificio" : "Building"}
            </label>
            <MultiSelect
              options={buildingOptions}
              value={selectedBuildingValues}
              onChange={handleBuildingChange}
              placeholder={locale === "es" ? "Edificios" : "Buildings"}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {locale === "es" ? "Tipo" : "Type"}
            </label>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              options={typeOptions}
              placeholder={locale === "es" ? "Tipo" : "Type"}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {locale === "es" ? "Unidad" : "Unit"}
            </label>
            <Select
              value={unitFilter}
              onChange={setUnitFilter}
              options={unitOptions}
              disabled={selectedCondoIds.length !== 1}
              placeholder={
                selectedCondoIds.length !== 1
                  ? locale === "es"
                    ? "Elegí un edificio"
                    : "Pick one building"
                  : locale === "es"
                    ? "Unidad"
                    : "Unit"
              }
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {locale === "es" ? "Proveedor" : "Provider"}
            </label>
            <Select
              value={providerFilter}
              onChange={setProviderFilter}
              options={providerOptions}
              placeholder={locale === "es" ? "Proveedor" : "Provider"}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <Spinner className="py-16" />
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-gray-500">
          {locale === "es"
            ? "No hay documentos con estos filtros."
            : "No documents match these filters."}
        </p>
      ) : (
        <ul className="space-y-4 ml-0 lg:ml-0 list-none p-0">
          {groups.map((g) => (
            <li
              key={groupKey(g)}
              className="border border-gray-200 rounded-lg p-4 shadow-sm bg-gray-50/50"
            >
              <GroupHeader group={g} locale={locale} />
              <ul className="mt-3 space-y-2 list-none p-0 border-t border-gray-200 pt-3">
                {g.versions.map((v) => (
                  <li
                    key={v.id}
                    className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 text-sm bg-white rounded border border-gray-100 p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">
                        {v.document.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {locale === "es"
                          ? "Fecha del documento"
                          : "Document date"}
                        : {v.date}
                        {g.type !== "spreadsheet" ? (
                          <>
                            {" · "}
                            {locale === "es" ? "Actualizado" : "Updated"}:{" "}
                            {formatTimestamp(v.updatedAt, locale)}
                          </>
                        ) : null}
                      </p>
                      {v.document.summary ? (
                        <p className="text-xs text-gray-600 mt-2 line-clamp-3">
                          {v.document.summary}
                        </p>
                      ) : null}
                    </div>
                    <VersionActions
                      fileUrl={v.document.fileUrl}
                      locale={locale}
                      deleting={deletingId === v.id}
                      onDelete={
                        g.type === "spreadsheet"
                          ? () => handleDeleteSpreadsheet(v.id)
                          : () => handleDeleteVersion(v.id)
                      }
                    />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      </div>
    </div>
  );
}

function VersionActions({
  fileUrl,
  locale,
  deleting,
  onDelete,
}: {
  fileUrl: string;
  locale: Locale;
  deleting: boolean;
  onDelete?: () => void;
}) {
  const deleteLabel = locale === "es" ? "Eliminar" : "Delete";
  const [opening, setOpening] = useState(false);
  const openingRef = useRef(false);

  const handleOpenClick = async (e: MouseEvent<HTMLButtonElement>) => {
    if (!fileUrl || openingRef.current) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    openingRef.current = true;
    setOpening(true);

    if (!isPresignedUrl(fileUrl)) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      openingRef.current = false;
      setOpening(false);
      return;
    }

    try {
      const refreshedUrl = await regenerateFileUrl(fileUrl);
      const targetUrl = refreshedUrl || fileUrl;
      window.open(targetUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to refresh document URL before opening:", err);
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    } finally {
      openingRef.current = false;
      setOpening(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 shrink-0">
      <button
        type="button"
        className={cn(
          "inline-flex items-center justify-center rounded font-medium transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary",
          "bg-primary text-white hover:bg-primary-light",
          opening && "pointer-events-none opacity-70",
          "px-2 py-1 text-xs lg:px-3 lg:py-1 lg:text-xs",
          "gap-1.5",
        )}
        aria-disabled={opening}
        onClick={handleOpenClick}
      >
        <ExternalLink className="w-4 h-4 shrink-0" />
        {opening
          ? locale === "es"
            ? "Abriendo..."
            : "Opening..."
          : locale === "es"
            ? "Abrir"
            : "Open"}
      </button>
      {onDelete ? (
        <button
          type="button"
          disabled={deleting}
          onClick={onDelete}
          title={deleteLabel}
          aria-label={deleteLabel}
          className={cn(
            "inline-flex items-center justify-center rounded-lg p-2",
            "text-red-600 hover:bg-red-50 hover:text-red-800",
            "disabled:opacity-50 disabled:pointer-events-none",
            "focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1",
          )}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ) : null}
    </div>
  );
}

function groupTypeLabel(group: CondoDocumentGroup, locale: Locale): string {
  const t = group.type;
  if (t === "common_expenses")
    return locale === "es" ? "Gastos comunes" : "Common expenses";
  if (t === "meeting_minutes")
    return locale === "es" ? "Acta de reunión" : "Meeting minutes";
  if (t === "bylaws") return locale === "es" ? "Reglamento" : "Bylaws";
  if (t === "lease_agreement")
    return locale === "es" ? "Contrato de alquiler" : "Lease agreement";
  if (t === "provider_invoice")
    return locale === "es" ? "Factura de proveedor" : "Provider invoice";
  if (t === "spreadsheet")
    return locale === "es" ? "Hoja de cálculo" : "Spreadsheet";
  if (t === "other" && group.otherType)
    return locale === "es"
      ? `Otro: ${group.otherType}`
      : `Other: ${group.otherType}`;
  if (t === "other") return locale === "es" ? "Otro" : "Other";
  return t;
}

function GroupHeader({
  group,
  locale,
}: {
  group: CondoDocumentGroup;
  locale: Locale;
}) {
  const typeLabel = groupTypeLabel(group, locale);

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-800">
      <span className="font-semibold">{group.condo.name}</span>
      <span className="text-gray-600">{typeLabel}</span>
      {group.unit ? (
        <span>
          {locale === "es" ? "Unidad" : "Unit"}: {group.unit.name}
        </span>
      ) : null}
      {group.provider ? (
        <span>
          {locale === "es" ? "Proveedor" : "Provider"}: {group.provider.name}
        </span>
      ) : null}
      <span className="text-gray-500 text-xs self-center">
        {group.versions.length}{" "}
        {group.versions.length === 1
          ? locale === "es"
            ? "versión"
            : "version"
          : locale === "es"
            ? "versiones"
            : "versions"}
      </span>
    </div>
  );
}
